const MODEL_VIEWER_SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
const QR_SRC = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.js';
const SET_GAP_CM = 8;

/* ---------- Helpers ---------- */

function track(name, detail = {}) {
  try {
    window.Shopify?.analytics?.publish?.(name, detail);
  } catch {}
  try {
    window.clarity?.('event', name);
  } catch {}
}

function parseSizeCm(label, landscape) {
  const match = String(label).match(/(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)\s*cm/);
  if (!match) return null;
  const a = Number(match[1]);
  const b = Number(match[2]);
  return landscape ? { w: Math.max(a, b), h: Math.min(a, b) } : { w: Math.min(a, b), h: Math.max(a, b) };
}

function shortSize(label) {
  return String(label).split(' (')[0];
}

function makeMoney(sample) {
  const match = sample.match(/\d[\d.,'\s\u00a0\u202f]*\d|\d/);
  if (!match) return (cents) => String(Math.round(cents / 100));
  const number = match[0];
  const decimalMatch = number.match(/([.,])(\d{2})$/);
  const decimalSep = decimalMatch ? decimalMatch[1] : null;
  const integerPart = decimalMatch ? number.slice(0, -3) : number;
  const thousandsMatch = integerPart.match(/\d([^\d])\d{3}$/);
  const thousandsSep = thousandsMatch ? thousandsMatch[1] : '';
  return (cents) => {
    const value = cents / 100;
    let integer = decimalSep ? Math.floor(value) : Math.round(value);
    let fraction = decimalSep ? Math.round((value - integer) * 100) : 0;
    if (fraction === 100) {
      integer += 1;
      fraction = 0;
    }
    const grouped = String(integer).replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    // Keep decimals whenever the shop format has them ("£236.00"), matching Liquid's money filter.
    const text = decimalSep ? `${grouped}${decimalSep}${String(fraction).padStart(2, '0')}` : grouped;
    return sample.replace(number, text);
  };
}

function addBusinessDays(date, days) {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added++;
  }
  return result;
}

function formatDay(date) {
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function loadScript(src, { module = false } = {}) {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing?.dataset.loaded) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', () => {
      script.dataset.loaded = '1';
      resolve();
    });
    script.addEventListener('error', reject);
    if (!existing) {
      if (module) script.type = 'module';
      script.src = src;
      document.head.append(script);
    }
  });
}

function cropStyle(crop) {
  if (!crop) return 'width:100%;height:100%;left:0;top:0;object-fit:cover;';
  return `width:${100 / crop.w}%;height:${100 / crop.h}%;left:${(-crop.x / crop.w) * 100}%;top:${(-crop.y / crop.h) * 100}%;`;
}

/* The decorators' rule of thumb: art spans about two-thirds of what sits beneath it, never more than three-quarters. */
function recommendSize(sizes, outer, widthCm) {
  const measured = sizes
    .map((size) => ({ outer: outer(size), size }))
    .filter((entry) => entry.outer)
    .sort((a, b) => a.outer.w - b.outer.w);
  if (!measured.length) return null;
  const target = widthCm * (2 / 3);
  const fitting = measured.filter((entry) => entry.outer.w <= widthCm * 0.75);
  const single = (fitting.length ? fitting : measured.slice(0, 1)).reduce((best, entry) =>
    Math.abs(entry.outer.w - target) < Math.abs(best.outer.w - target) ? entry : best,
  );
  const share = single.outer.w / widthCm;
  if (share >= 0.4) return { share, single: single.size };
  const set = measured
    .map((entry) => ({ size: entry.size, spanCm: entry.outer.w * 3 + SET_GAP_CM * 2 }))
    .filter((option) => option.spanCm <= widthCm * 0.75)
    .reduce((best, option) => (!best || Math.abs(option.spanCm - target) < Math.abs(best.spanCm - target) ? option : best), null);
  return { set, share, single: single.size };
}

/* ---------- Binary glTF of a print hanging on a wall (back face at z = 0, photograph facing +z) ---------- */

function hexToLinear(hex) {
  const value = Number.parseInt(hex.replace('#', ''), 16);
  const channel = (shift) => {
    const srgb = ((value >> shift) & 0xff) / 255;
    return srgb <= 0.04045 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  return [channel(16), channel(8), channel(0), 1];
}

function buildPrintGlb({ widthM, heightM, depthM, edgeColor, image }) {
  const x = widthM / 2;
  const y = heightM / 2;
  const z = depthM;
  const quad = (target, corners, normal) => {
    const base = target.positions.length / 3;
    corners.forEach((corner) => {
      target.positions.push(...corner);
      target.normals.push(...normal);
    });
    target.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };
  const front = { indices: [], material: 0, normals: [], positions: [], uvs: [0, 1, 1, 1, 1, 0, 0, 0] };
  quad(front, [[-x, -y, z], [x, -y, z], [x, y, z], [-x, y, z]], [0, 0, 1]);
  const edges = { indices: [], material: 1, normals: [], positions: [] };
  quad(edges, [[-x, y, z], [x, y, z], [x, y, 0], [-x, y, 0]], [0, 1, 0]);
  quad(edges, [[-x, -y, 0], [x, -y, 0], [x, -y, z], [-x, -y, z]], [0, -1, 0]);
  quad(edges, [[x, -y, z], [x, -y, 0], [x, y, 0], [x, y, z]], [1, 0, 0]);
  quad(edges, [[-x, -y, 0], [-x, -y, z], [-x, y, z], [-x, y, 0]], [-1, 0, 0]);
  quad(edges, [[x, -y, 0], [-x, -y, 0], [-x, y, 0], [x, y, 0]], [0, 0, -1]);

  const chunks = [];
  const bufferViews = [];
  const accessors = [];
  let byteLength = 0;
  const pad = () => {
    const padding = (4 - (byteLength % 4)) % 4;
    if (padding) {
      chunks.push(new Uint8Array(padding));
      byteLength += padding;
    }
  };
  const addView = (bytes, target) => {
    pad();
    chunks.push(bytes);
    bufferViews.push({ buffer: 0, byteLength: bytes.byteLength, byteOffset: byteLength, ...(target ? { target } : {}) });
    byteLength += bytes.byteLength;
    return bufferViews.length - 1;
  };
  const bounds = (values) => {
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < values.length; i += 3) {
      for (let axis = 0; axis < 3; axis++) {
        min[axis] = Math.min(min[axis], values[i + axis]);
        max[axis] = Math.max(max[axis], values[i + axis]);
      }
    }
    return { min, max };
  };
  const floats = (values, type, withBounds) => {
    const view = addView(new Uint8Array(new Float32Array(values).buffer), 34962);
    accessors.push({ bufferView: view, componentType: 5126, count: values.length / (type === 'VEC2' ? 2 : 3), type, ...(withBounds ? bounds(values) : {}) });
    return accessors.length - 1;
  };
  const indices = (values) => {
    const view = addView(new Uint8Array(new Uint16Array(values).buffer), 34963);
    accessors.push({ bufferView: view, componentType: 5123, count: values.length, type: 'SCALAR' });
    return accessors.length - 1;
  };
  const primitives = [front, edges].map((p) => ({
    attributes: {
      NORMAL: floats(p.normals, 'VEC3'),
      POSITION: floats(p.positions, 'VEC3', true),
      ...(p.uvs ? { TEXCOORD_0: floats(p.uvs, 'VEC2') } : {}),
    },
    indices: indices(p.indices),
    material: p.material,
  }));
  const imageView = addView(image);
  pad();
  const json = {
    accessors,
    asset: { generator: 'kakahuette-ar', version: '2.0' },
    bufferViews,
    buffers: [{ byteLength }],
    images: [{ bufferView: imageView, mimeType: 'image/jpeg' }],
    materials: [
      { name: 'photograph', pbrMetallicRoughness: { baseColorTexture: { index: 0 }, metallicFactor: 0, roughnessFactor: 0.85 } },
      { name: 'edge', pbrMetallicRoughness: { baseColorFactor: hexToLinear(edgeColor), metallicFactor: 0, roughnessFactor: 0.8 } },
    ],
    meshes: [{ name: 'print', primitives }],
    nodes: [{ mesh: 0, name: 'print' }],
    samplers: [{ magFilter: 9729, minFilter: 9987, wrapS: 33071, wrapT: 33071 }],
    scene: 0,
    scenes: [{ nodes: [0] }],
    textures: [{ sampler: 0, source: 0 }],
  };
  const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
  const jsonPadded = new Uint8Array(Math.ceil(jsonBytes.byteLength / 4) * 4).fill(0x20);
  jsonPadded.set(jsonBytes);
  const total = 12 + 8 + jsonPadded.byteLength + 8 + byteLength;
  const output = new Uint8Array(total);
  const view = new DataView(output.buffer);
  view.setUint32(0, 0x46546c67, true);
  view.setUint32(4, 2, true);
  view.setUint32(8, total, true);
  view.setUint32(12, jsonPadded.byteLength, true);
  view.setUint32(16, 0x4e4f534a, true);
  output.set(jsonPadded, 20);
  let offset = 20 + jsonPadded.byteLength;
  view.setUint32(offset, byteLength, true);
  view.setUint32(offset + 4, 0x004e4942, true);
  offset += 8;
  chunks.forEach((chunk) => {
    output.set(chunk, offset);
    offset += chunk.byteLength;
  });
  return output;
}

/* ---------- Main element ---------- */

class KkhPrint extends HTMLElement {
  connectedCallback() {
    if (this.config) return;
    this.config = JSON.parse(this.querySelector('[data-kkh-config]').textContent);
    const c = this.config;
    this.money = makeMoney(c.moneySample);
    this.sizes = c.sizeOption ? [...new Set(c.variants.map((v) => v.options[c.sizeOption - 1]))] : [];
    this.formats = c.frameOption ? [...new Set(c.variants.map((v) => v.options[c.frameOption - 1]))] : [];
    const params = new URLSearchParams(location.search);
    const fromUrl = c.variants.find((v) => String(v.id) === params.get('variant'));
    const initial = fromUrl ?? c.variants.find((v) => v.id === c.selectedVariantId) ?? c.variants[0];
    this.state = {
      size: c.sizeOption ? initial.options[c.sizeOption - 1] : null,
      format: c.frameOption ? initial.options[c.frameOption - 1] : null,
      variantId: initial.id,
    };
    this.querySelector('[data-kkh-variant-select]')?.addEventListener('change', (event) => {
      this.state.variantId = Number(event.target.value);
      this.render();
    });
    this.form = this.querySelector('form.kkh-form');
    this.actions = this.querySelector('[data-kkh-actions]');
    this.sticky = this.querySelector('[data-kkh-sticky]');
    this.sheets = Object.fromEntries([...this.querySelectorAll('[data-kkh-sheet]')].map((d) => [d.dataset.kkhSheet, d]));
    this.wallTab = 'camera';

    this.addEventListener('click', (event) => this.onClick(event));
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.querySelector('[data-kkh-set-add]')?.addEventListener('click', () => this.addSet());
    this.setupGallery();
    this.setupFinder();
    this.setupSheets();
    this.setupSticky();
    this.setupRoom();
    this.render({ replaceUrl: false });

    if (location.hash === '#wall') requestAnimationFrame(() => this.openSheet('wall', { tab: 'camera' }));
  }

  get framed() {
    return this.state.format === this.config.framedValue;
  }

  variantFor(size = this.state.size, format = this.state.format) {
    const c = this.config;
    return c.variants.find(
      (v) => (!c.sizeOption || v.options[c.sizeOption - 1] === size) && (!c.frameOption || v.options[c.frameOption - 1] === format),
    );
  }

  get variant() {
    if (!this.config.sizeOption && !this.config.frameOption) {
      return this.config.variants.find((v) => v.id === this.state.variantId) ?? this.config.variants[0];
    }
    return this.variantFor();
  }

  printCm(size = this.state.size) {
    return parseSizeCm(size, this.config.landscape);
  }

  outerCm(size = this.state.size, framed = this.framed) {
    const cm = this.printCm(size);
    if (!cm) return null;
    const border = framed ? (this.config.frame.frameCm + this.config.frame.matCm) * 2 : 0;
    return { w: cm.w + border, h: cm.h + border };
  }

  select({ size, format }) {
    if (size && this.sizes.includes(size)) {
      this.state.size = size;
      this.roomSize = size;
    }
    if (format && this.formats.includes(format)) this.state.format = format;
    this.render();
  }

  onClick(event) {
    const sizeButton = event.target.closest('[data-kkh-size]');
    if (sizeButton) {
      this.select({ size: sizeButton.dataset.kkhSize });
      track('kkh_size_select', { size: sizeButton.dataset.kkhSize });
      return;
    }
    const roomSizeButton = event.target.closest('[data-kkh-room-pick]');
    if (roomSizeButton) {
      this.roomSize = roomSizeButton.dataset.kkhRoomPick;
      this.renderRoom();
      this.renderSet();
      track('kkh_room_size', { size: this.roomSize });
      return;
    }
    if (event.target.closest('[data-kkh-room-choose]')) {
      const size = this.roomSize ?? this.state.size;
      if (size !== this.state.size) {
        this.select({ size });
        track('kkh_room_choose', { size });
        (this.actions ?? this.form)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        track('kkh_room_add', { size });
        this.form?.requestSubmit();
      }
      return;
    }
    const roomButton = event.target.closest('[data-kkh-room-key]');
    if (roomButton) {
      this.roomKey = roomButton.dataset.kkhRoomKey;
      this.querySelectorAll('[data-kkh-room-key]').forEach((el) => el.setAttribute('aria-checked', String(el === roomButton)));
      this.renderRoom();
      track('kkh_room_select', { room: this.roomKey });
      return;
    }
    const formatButton = event.target.closest('[data-kkh-format]');
    if (formatButton) {
      this.select({ format: formatButton.dataset.kkhFormat });
      track('kkh_format_select', { format: formatButton.dataset.kkhFormat });
      return;
    }
    const opener = event.target.closest('[data-kkh-open]');
    if (opener) {
      this.openSheet(opener.dataset.kkhOpen, { tab: opener.dataset.kkhWallTab });
      return;
    }
    const tab = event.target.closest('[data-kkh-tab]');
    if (tab) {
      this.setWallTab(tab.dataset.kkhTab);
      return;
    }
    if (event.target.closest('[data-kkh-close]')) {
      const dialog = event.target.closest('dialog');
      if (dialog === this.viewer) this.viewer.close();
      else this.closeSheet(dialog);
      return;
    }
    const zoom = event.target.closest('[data-kkh-zoom]');
    if (zoom) {
      this.openViewer(Number(zoom.dataset.kkhZoom));
      return;
    }
    const galleryArrow = event.target.closest('[data-kkh-gal]');
    if (galleryArrow) {
      const n = this.slideCount ?? 1;
      this.goToSlide(Math.max(0, Math.min(n - 1, (this.slideIndex ?? 0) + Number(galleryArrow.dataset.kkhGal))));
      return;
    }
    const viewerArrow = event.target.closest('[data-kkh-vnav]');
    if (viewerArrow) {
      this.viewerStep(Number(viewerArrow.dataset.kkhVnav));
      return;
    }
    const thumb = event.target.closest('[data-kkh-thumb]');
    if (thumb) this.goToSlide(Number(thumb.dataset.kkhThumb));
  }

  /* ---------- Rendering ---------- */

  render({ replaceUrl = true } = {}) {
    const variant = this.variant;
    const { size, format } = this.state;
    const c = this.config;
    const available = Boolean(variant?.available);

    this.querySelectorAll('[data-kkh-size]').forEach((el) => {
      el.setAttribute('aria-checked', String(el.dataset.kkhSize === size));
      const price = el.querySelector('[data-kkh-size-price]');
      const v = this.variantFor(el.dataset.kkhSize, format);
      if (price) price.textContent = v ? (v.available ? v.priceText : `${v.priceText}, sold out`) : '';
      const scale = el.querySelector('[data-kkh-scale]');
      if (scale) this.paintScale(scale, el.dataset.kkhSize);
    });

    const sizeButton = [...this.querySelectorAll('.kkh-size[data-kkh-size]')].find((el) => el.dataset.kkhSize === size);
    this.querySelectorAll('[data-kkh-size-detail]').forEach((el) => {
      if (!sizeButton) return;
      const { kkhInches: inches, kkhNote: note } = sizeButton.dataset;
      el.innerHTML = `<strong>${shortSize(size)}</strong>${inches ? ` <span>(${inches})</span>` : ''}${note ? ` · ${note}` : ''}`;
    });

    this.querySelectorAll('[data-kkh-format]').forEach((el) => {
      el.setAttribute('aria-checked', String(el.dataset.kkhFormat === format));
      const meta = el.querySelector('[data-kkh-format-meta]');
      if (meta) meta.textContent = c.formatNotes?.[el.dataset.kkhFormat]?.lead ?? '';
      const extra = el.querySelector('[data-kkh-format-extra]');
      if (!extra) return;
      const base = this.variantFor(size, this.formats.find((f) => f !== c.framedValue));
      const own = this.variantFor(size, el.dataset.kkhFormat);
      extra.textContent = el.dataset.kkhFormat === c.framedValue && base && own ? `+${this.money(own.price - base.price)}` : '';
    });

    const note = c.formatNotes?.[format];
    this.querySelectorAll('[data-kkh-format-note]').forEach((el) => {
      el.innerHTML = note ? `<strong>${note.lead}</strong> ${note.note}` : '';
    });

    const priceText = variant?.priceText ?? '';
    this.querySelectorAll('[data-kkh-price]').forEach((el) => {
      if (el.textContent !== priceText) {
        el.textContent = priceText;
        el.animate?.([{ opacity: 0.35, transform: 'translateY(3px)' }, { opacity: 1, transform: 'none' }], { duration: 260, easing: 'ease-out' });
      }
    });
    this.querySelectorAll('[data-kkh-format-label]').forEach((el) => {
      el.textContent = format ? (this.framed ? 'framed' : 'print only') : '';
    });
    this.querySelectorAll('[data-kkh-add-price]').forEach((el) => (el.textContent = available ? priceText : ''));
    this.querySelectorAll('[data-kkh-add-label]').forEach((el) => (el.textContent = available ? 'Add to cart' : 'Sold out'));
    this.querySelectorAll('[data-kkh-add]').forEach((el) => {
      el.disabled = !available;
    });
    const input = this.querySelector('[data-kkh-variant-input]');
    if (input && variant) input.value = variant.id;

    const summary = [size && shortSize(size), format && (this.framed ? 'Framed' : 'Print only')].filter(Boolean).join(' · ');
    this.querySelectorAll('[data-kkh-summary]').forEach((el) => (el.textContent = summary));
    this.querySelectorAll('[data-kkh-size-short]').forEach((el) => (el.textContent = size ? shortSize(size) : ''));
    this.querySelectorAll('[data-kkh-framed-copy]').forEach((el) => (el.textContent = this.framed ? ', framed,' : ''));

    this.renderDelivery();
    this.renderScene();
    this.querySelectorAll('[data-kkh-face]').forEach((el) =>
      this.paintFace(el, el.closest('[data-kkh-room-print]') ? (this.roomSize ?? this.state.size) : this.state.size),
    );
    this.renderFinder();
    this.renderSet();
    this.photoWall?.refresh();
    if (this.sheets.wall?.open && this.wallTab === 'camera') this.loadAr();

    if (replaceUrl && variant) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', url);
    }
  }

  paintScale(el, size) {
    const largest = this.sizes.map((s) => this.printCm(s)).filter(Boolean).reduce((m, cm) => Math.max(m, cm.w, cm.h), 1);
    const cm = this.printCm(size);
    if (!cm) return;
    el.style.setProperty('--w', (cm.w / largest).toFixed(3));
    el.style.setProperty('--h', (cm.h / largest).toFixed(3));
  }

  renderDelivery() {
    const d = this.config.delivery;
    if (!d) return;
    const home = this.config.country === d.homeCountry;
    const dispatch = this.framed ? d.framedDispatch : d.dispatch;
    const transit = home ? d.transitHome : d.transitWorld;
    const arrives = addBusinessDays(new Date(), dispatch + transit);
    let where = '';
    if (!home && this.config.country) {
      try {
        where = ` to ${new Intl.DisplayNames(['en'], { type: 'region' }).of(this.config.country)}`;
      } catch {}
    }
    // Framed pieces ship free to these countries (made locally by creativehub, or in Hong Kong).
    // Elsewhere checkout charges HK$500 (20 × 30, 30 × 45) or HK$900 (40 × 60, 60 × 90): the Shopify
    // "Framed small" / "Framed large" shipping profiles, set 5 Nov 2026. Keep the two in step.
    const FRAMED_FREE = ['HK', 'GB', 'IE', 'DE', 'FR', 'AT', 'IT', 'ES', 'FI', 'PL', 'NO', 'GG', 'US', 'CA'];
    let shipping = 'Free tracked delivery';
    if (this.framed && this.config.country && !FRAMED_FREE.includes(this.config.country)) {
      const cm = this.printCm();
      const feeHkd = cm && Math.max(cm.w, cm.h) >= 60 ? 900 : 500;
      const rate = Number(window.Shopify?.currency?.rate) || 1;
      shipping = `Tracked delivery ${this.money(Math.round(feeHkd * rate) * 100)}`;
    }
    const text = this.framed
      ? `${shipping}${where}. Made to order, arrives by ${formatDay(arrives)}.`
      : `${shipping}${where}. Order today, arrives by ${formatDay(arrives)}.`;
    this.querySelectorAll('[data-kkh-delivery]').forEach((el) => (el.textContent = text));
  }

  /* ---------- Room preview: the print to scale on a real room photograph ---------- */

  setupRoom() {
    this.roomKey = 'living';
    const pick = this.config.pickSize;
    this.roomSize = pick && this.sizes.includes(pick) && !new URLSearchParams(location.search).has('variant') ? pick : null;
    this.roomEl = this.querySelector('[data-kkh-room]');
    if (!this.roomEl || !this.config.rooms) return;
    this.roomMobile = matchMedia('(max-width: 989px)');
    this.roomMobile.addEventListener?.('change', () => this.renderRoom());
  }

  renderRoom() {
    const host = this.roomEl;
    const room = this.config.rooms?.[this.roomKey];
    const size = this.roomSize ?? this.state.size;
    const outer = this.outerCm(size);
    if (!host || !room || !outer) return;
    const [cx0, cy0, cw, ch] = room[this.roomMobile?.matches ? 'mobile' : 'desktop'];
    host.style.aspectRatio = `${cw} / ${ch}`;
    const stage = host.querySelector('[data-kkh-room-stage]');
    Object.assign(stage.style, {
      width: `${(room.w / cw) * 100}%`,
      height: `${(room.h / ch) * 100}%`,
      left: `${(-cx0 / cw) * 100}%`,
      top: `${(-cy0 / ch) * 100}%`,
    });
    const img = host.querySelector('[data-kkh-room-img]');
    if (img.getAttribute('src') !== room.src) {
      img.width = room.w;
      img.height = room.h;
      img.src = room.src;
    }
    // Hang 22 cm above the furniture, or centred at eye level on an open wall.
    const w = outer.w / room.cmPerPx;
    const h = outer.h / room.cmPerPx;
    const bottom = room.base ? room.base - 22 / room.cmPerPx : room.floor - room.eye / room.cmPerPx + h / 2;
    Object.assign(host.querySelector('[data-kkh-room-print]').style, {
      left: `${((room.cx - w / 2) / room.w) * 100}%`,
      top: `${((bottom - h) / room.h) * 100}%`,
      width: `${(w / room.w) * 100}%`,
      height: `${(h / room.h) * 100}%`,
    });
    host.querySelector('[data-kkh-room-size]').textContent = `${shortSize(size)}${this.framed ? `, ${Math.round(outer.w)} × ${Math.round(outer.h)} cm framed` : ''}`;
    host.querySelector('[data-kkh-room-note]').textContent = `${room.note} · approximate scale`;
    const face = host.querySelector('[data-kkh-face]');
    if (face) this.paintFace(face, size);
    this.querySelectorAll('[data-kkh-room-size-short]').forEach((el) => (el.textContent = shortSize(size)));
    this.querySelectorAll('[data-kkh-room-pick]').forEach((el) => el.setAttribute('aria-checked', String(el.dataset.kkhRoomPick === size)));
    const chosen = size === this.state.size;
    const v = this.variantFor(size, this.state.format);
    this.querySelectorAll('[data-kkh-room-choose-label]').forEach((el) => (el.textContent = chosen ? `Add ${shortSize(size)} to cart` : `Choose ${shortSize(size)}`));
    this.querySelectorAll('[data-kkh-room-choose-price]').forEach((el) => (el.textContent = v?.priceText ?? ''));
    this.querySelectorAll('[data-kkh-room-choose]').forEach((el) => (el.disabled = !v?.available));
  }

  renderScene() {
    this.renderRoom();
  }

  paintFace(el, size = this.state.size, art = this.config.art) {
    const { frame } = this.config;
    const print = this.printCm(size);
    if (!print || !art) return;
    const outer = this.outerCm(size);
    const framed = this.framed;
    if (!el.firstChild) {
      el.innerHTML = `<span class="kkh-face__mat"></span><span class="kkh-face__photo"><img alt="" draggable="false" src="${art.url}" style="${cropStyle(art.crop)}"></span>`;
    }
    const mat = el.querySelector('.kkh-face__mat');
    const photo = el.querySelector('.kkh-face__photo');
    const inset = (cmX, cmY) => `${(cmY / outer.h) * 100}% ${(cmX / outer.w) * 100}%`;
    el.style.background = framed ? frame.frameColor : 'transparent';
    mat.style.display = framed ? 'block' : 'none';
    mat.style.inset = inset(frame.frameCm, frame.frameCm);
    photo.style.inset = framed ? inset(frame.frameCm + frame.matCm, frame.frameCm + frame.matCm) : '0';
  }

  /* ---------- Gallery ---------- */

  setupGallery() {
    this.track = this.querySelector('[data-kkh-track]');
    this.thumbs = [...this.querySelectorAll('[data-kkh-thumb]')];
    this.viewer = this.querySelector('[data-kkh-viewer]');
    if (!this.track) return;
    let frame = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (index !== this.slideIndex) {
            this.slideIndex = index;
            this.updateGalleryUi();
            this.thumbs.forEach((t, i) => t.setAttribute('aria-selected', String(i === index)));
            this.thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
            if (index > 0 && !this.galleryTracked) {
              this.galleryTracked = true;
              track('kkh_gallery_swipe');
            }
          }
        });
      },
      { passive: true },
    );
    this.slideCount = this.track.querySelectorAll('[data-kkh-slide]').length;
    this.slideIndex = 0;
    this.updateGalleryUi();
    this.nudgeGallery();
  }

  updateGalleryUi() {
    const i = this.slideIndex ?? 0;
    const n = this.slideCount ?? 1;
    const count = this.querySelector('[data-kkh-count]');
    if (count) count.textContent = n > 1 ? `${i + 1} / ${n}` : '';
    const prev = this.querySelector('.kkh-gallery__stage [data-kkh-gal="-1"]');
    const next = this.querySelector('.kkh-gallery__stage [data-kkh-gal="1"]');
    if (prev) prev.hidden = i <= 0;
    if (next) next.hidden = i >= n - 1;
    const zoom = this.querySelector('[data-kkh-zoom-current]');
    if (zoom) zoom.dataset.kkhZoom = String(i);
  }

  // Once per visit, slide the gallery a little and back so phone visitors see there is more to swipe.
  nudgeGallery() {
    if (!this.track || this.slideCount < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!matchMedia('(max-width: 989px)').matches) return;
    try {
      if (sessionStorage.getItem('kkh-nudged')) return;
      sessionStorage.setItem('kkh-nudged', '1');
    } catch {}
    let touched = false;
    this.track.addEventListener('pointerdown', () => (touched = true), { once: true });
    setTimeout(() => {
      if (touched || this.slideIndex) return;
      const slides = [...this.track.children];
      slides.forEach((el) => el.animate([{ transform: 'none' }, { transform: 'translateX(-56px)' }, { transform: 'none' }], { duration: 900, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }));
    }, 1600);
  }

  goToSlide(index) {
    this.track?.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
  }

  openViewer(index) {
    if (!this.viewer) return;
    const trackEl = this.viewer.querySelector('[data-kkh-viewer-track]');
    if (!trackEl.childElementCount) {
      const { art } = this.config;
      this.querySelectorAll('[data-kkh-slide]').forEach((slide, i) => {
        const item = document.createElement('div');
        item.className = 'kkh-viewer__slide';
        if (i === 0 && art?.crop) {
          const aspect = art.crop.w / art.crop.h;
          item.innerHTML = `<span class="kkh-art kkh-art--crop kkh-viewer__zoom" data-kkh-zoomable style="aspect-ratio:${aspect};width:min(100vw,${aspect * 100}svh);height:auto"><img alt="" draggable="false" src="${art.url}" style="${cropStyle(art.crop)}"></span>`;
        } else {
          const img = slide.querySelector('img');
          // Ask the CDN for the largest version it has (the original file caps it).
          let src = img?.currentSrc || img?.src || '';
          src = src.replace(/([?&])width=\d+/, '$1width=2400');
          item.innerHTML = `<img class="kkh-viewer__zoom" data-kkh-zoomable alt="${img?.alt ?? ''}" draggable="false" src="${src}">`;
        }
        trackEl.append(item);
      });
      this.setupViewerZoom(trackEl);
      trackEl.addEventListener('scroll', () => {
        const i = Math.round(trackEl.scrollLeft / trackEl.clientWidth);
        if (i !== this.viewerIndex) {
          this.viewerIndex = i;
          this.resetZoom();
          this.updateViewerUi();
        }
      }, { passive: true });
    }
    this.viewer.showModal();
    this.lockScroll(true);
    trackEl.scrollLeft = index * trackEl.clientWidth;
    this.viewerIndex = index;
    this.updateViewerUi();
    this.viewer.addEventListener('close', () => {
      this.resetZoom();
      this.lockScroll(false);
    }, { once: true });
    track('kkh_zoom_open');
  }

  viewerStep(direction) {
    const trackEl = this.viewer?.querySelector('[data-kkh-viewer-track]');
    if (!trackEl) return;
    const n = trackEl.childElementCount;
    const i = Math.max(0, Math.min(n - 1, (this.viewerIndex ?? 0) + direction));
    this.resetZoom();
    trackEl.scrollTo({ left: i * trackEl.clientWidth, behavior: 'smooth' });
  }

  updateViewerUi() {
    const trackEl = this.viewer?.querySelector('[data-kkh-viewer-track]');
    if (!trackEl) return;
    const n = trackEl.childElementCount;
    const i = this.viewerIndex ?? 0;
    const count = this.viewer.querySelector('[data-kkh-vcount]');
    if (count) count.textContent = `${i + 1} / ${n}`;
    this.viewer.querySelector('[data-kkh-vnav="-1"]').hidden = i <= 0;
    this.viewer.querySelector('[data-kkh-vnav="1"]').hidden = i >= n - 1;
  }

  /* Tap or click zooms 2.5× at that point; drag pans; pinch zooms on touch screens; tap again to zoom out. */
  setupViewerZoom(trackEl) {
    this.zoom = { el: null, s: 1, x: 0, y: 0 };
    const pointers = new Map();
    let start = null;
    let moved = false;
    const base = (el) => {
      const r = el.getBoundingClientRect();
      return { left: r.left - this.zoom.x, top: r.top - this.zoom.y, w: r.width / this.zoom.s, h: r.height / this.zoom.s };
    };
    const apply = () => {
      const z = this.zoom;
      if (!z.el) return;
      const b = base(z.el);
      // keep the image covering its own box: no panning past the edges
      z.x = Math.min(0, Math.max(b.w * (1 - z.s), z.x));
      z.y = Math.min(0, Math.max(b.h * (1 - z.s), z.y));
      z.el.style.transform = `translate(${z.x}px, ${z.y}px) scale(${z.s})`;
      trackEl.classList.toggle('is-zoomed', z.s > 1.01);
      const hint = this.viewer.querySelector('[data-kkh-vhint]');
      if (hint) hint.textContent = z.s > 1.01 ? 'Drag to look around, tap to zoom out' : 'Tap to zoom, pinch on a phone';
    };
    const zoomTo = (el, s, cx, cy) => {
      const z = this.zoom;
      if (z.el && z.el !== el) this.resetZoom();
      z.el = el;
      const b = base(el);
      const u = (cx - b.left - z.x) / z.s;
      const v = (cy - b.top - z.y) / z.s;
      z.s = Math.max(1, Math.min(4, s));
      z.x = cx - b.left - u * z.s;
      z.y = cy - b.top - v * z.s;
      apply();
    };
    this.resetZoom = () => {
      const z = this.zoom;
      if (z?.el) z.el.style.transform = '';
      if (z) Object.assign(z, { el: null, s: 1, x: 0, y: 0 });
      trackEl.classList.remove('is-zoomed');
    };
    trackEl.addEventListener('pointerdown', (e) => {
      const el = e.target.closest('[data-kkh-zoomable]');
      if (!el) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      moved = false;
      if (pointers.size === 1) start = { x: e.clientX, y: e.clientY, zx: this.zoom.x, zy: this.zoom.y };
      if (pointers.size === 2) {
        const [p1, p2] = [...pointers.values()];
        start = { dist: Math.hypot(p1.x - p2.x, p1.y - p2.y), s: this.zoom.el === el ? this.zoom.s : 1, el };
      }
      if (this.zoom.s > 1.01 || pointers.size === 2) el.setPointerCapture?.(e.pointerId);
    });
    trackEl.addEventListener('pointermove', (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2 && start?.dist) {
        const [p1, p2] = [...pointers.values()];
        moved = true;
        zoomTo(start.el, (start.s * Math.hypot(p1.x - p2.x, p1.y - p2.y)) / start.dist, (p1.x + p2.x) / 2, (p1.y + p2.y) / 2);
      } else if (pointers.size === 1 && this.zoom.s > 1.01 && start) {
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) moved = true;
        this.zoom.x = start.zx + dx;
        this.zoom.y = start.zy + dy;
        apply();
      }
    });
    const end = (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      if (pointers.size === 0 && !moved && start && !start.dist) {
        const el = e.target.closest('[data-kkh-zoomable]');
        if (el) {
          if (this.zoom.s > 1.01) this.resetZoom();
          else {
            zoomTo(el, 2.5, e.clientX, e.clientY);
            track('kkh_zoom_in');
          }
        }
      }
      if (pointers.size === 0) start = null;
    };
    trackEl.addEventListener('pointerup', end);
    trackEl.addEventListener('pointercancel', (e) => pointers.delete(e.pointerId));
    trackEl.addEventListener('wheel', (e) => {
      const el = e.target.closest('[data-kkh-zoomable]');
      if (!el || !e.ctrlKey) return;
      e.preventDefault();
      zoomTo(el, this.zoom.s * (1 - e.deltaY / 200), e.clientX, e.clientY);
    }, { passive: false });
  }

  /* ---------- Sheets ---------- */

  setupSheets() {
    Object.values(this.sheets).forEach((dialog) => {
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeSheet(dialog);
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) this.closeSheet(dialog);
      });
      const panel = dialog.querySelector('[data-kkh-panel]');
      let startY = 0;
      let startTime = 0;
      let dy = 0;
      let dragging = false;
      dialog.querySelectorAll('[data-kkh-drag]').forEach((handle) => {
        handle.addEventListener('pointerdown', (event) => {
          if (event.target.closest('button') || window.matchMedia('(min-width: 990px)').matches) return;
          dragging = true;
          startY = event.clientY;
          startTime = performance.now();
          dy = 0;
          dialog.setAttribute('data-dragging', '');
          handle.setPointerCapture(event.pointerId);
        });
        handle.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          dy = Math.max(0, event.clientY - startY);
          panel.style.setProperty('--drag', `${dy}px`);
        });
        const end = () => {
          if (!dragging) return;
          dragging = false;
          dialog.removeAttribute('data-dragging');
          const velocity = dy / Math.max(1, performance.now() - startTime);
          panel.style.removeProperty('--drag');
          if (dy > 110 || velocity > 0.6) this.closeSheet(dialog);
        };
        handle.addEventListener('pointerup', end);
        handle.addEventListener('pointercancel', end);
      });
    });
  }

  openSheet(name, { tab } = {}) {
    const dialog = this.sheets[name];
    if (!dialog) return;
    Object.values(this.sheets).forEach((d) => d !== dialog && d.open && this.closeSheet(d, { immediate: true }));
    if (name === 'wall') this.setWallTab(tab ?? this.wallTab);
    if (!dialog.open) dialog.showModal();
    this.lockScroll(true);
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.setAttribute('data-state', 'open')));
    this.updateSticky();
    track(name === 'wall' ? 'kkh_wall_open' : 'kkh_sheet_open', { tab: tab ?? null });
  }

  closeSheet(dialog, { immediate = false } = {}) {
    if (!dialog?.open) return;
    dialog.removeAttribute('data-state');
    const finish = () => {
      dialog.close();
      if (!Object.values(this.sheets).some((d) => d.open)) this.lockScroll(false);
      this.updateSticky();
    };
    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else setTimeout(finish, 380);
  }

  lockScroll(locked) {
    document.documentElement.style.overflow = locked ? 'hidden' : '';
  }

  /* ---------- Sticky bar ---------- */

  setupSticky() {
    if (!this.sticky || !this.actions) return;
    let frame = 0;
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(() => {
        frame = 0;
        this.updateSticky();
      });
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    this.updateSticky();
  }

  updateSticky() {
    if (!this.sticky || !this.actions) return;
    // Shown whenever the main buttons are off screen: before the visitor reaches them and after they scroll past,
    // so price and Add to cart are in the first screen on a phone.
    const rect = this.actions.getBoundingClientRect();
    const actionsVisible = rect.top < window.innerHeight && rect.bottom > 0;
    const sheetOpen = Object.values(this.sheets).some((d) => d.open);
    const footer = document.querySelector('footer, .site-footer, #shopify-section-footer');
    const nearFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
    this.sticky.hidden = actionsVisible || sheetOpen || nearFooter;
  }

  /* ---------- Cart ---------- */

  async addToCart(items) {
    const buttons = [...this.querySelectorAll('[data-kkh-add], [data-kkh-set-add]')];
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    const error = this.querySelector('[data-kkh-error]');
    if (error) error.hidden = true;
    try {
      let cart = null;
      try {
        cart = (await import('cart-store')).default;
      } catch {}
      if (cart) {
        await cart.add(items.length === 1 ? items[0] : items);
      } else {
        const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
          body: JSON.stringify({ items }),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        });
        if (!response.ok) throw new Error((await response.json()).description ?? 'Could not add to cart');
      }
      Object.values(this.sheets).forEach((d) => this.closeSheet(d, { immediate: true }));
      const drawer = document.querySelector('.modal--cart, .popup--cart-notification');
      if (cart && typeof drawer?.open === 'function') {
        await cart.resetVariantsBeingAdded?.();
        if (!drawer.shown) drawer.open();
      } else {
        location.href = `${window.Shopify?.routes?.root ?? '/'}cart`;
      }
    } catch (err) {
      if (error) {
        error.textContent = err?.message || 'Something went wrong. Please try again.';
        error.hidden = false;
      }
    } finally {
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  }

  onSubmit(event) {
    event.preventDefault();
    const variant = this.variant;
    if (!variant?.available) return;
    track('kkh_add_to_cart', { variant: variant.id, size: this.state.size, format: this.state.format, from: event.submitter?.closest('dialog')?.dataset.kkhSheet ?? (event.submitter?.closest('[data-kkh-sticky]') ? 'sticky' : 'page') });
    this.addToCart([{ id: variant.id, quantity: 1 }]);
  }

  /* ---------- Size finder ---------- */

  setupFinder() {
    this.finder = this.querySelector('[data-kkh-finder]');
    if (!this.finder) return;
    this.finderWidth = 200;
    const range = this.finder.querySelector('[data-kkh-width]');
    this.finder.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-kkh-furniture]');
      if (chip) {
        this.finderWidth = Number(chip.dataset.kkhFurniture);
        range.value = this.finderWidth;
        this.finderPreset = chip.dataset.kkhFurniture;
        this.renderFinder();
        track('kkh_finder_use', { width: this.finderWidth });
        return;
      }
      const apply = event.target.closest('[data-kkh-apply-size]');
      if (apply) {
        this.select({ size: apply.dataset.kkhApplySize });
        return;
      }
      if (event.target.closest('[data-kkh-see-set]')) {
        const set = this.querySelector('[data-kkh-set]');
        if (this.finderSetSize) this.select({ size: this.finderSetSize });
        set?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        track('kkh_finder_set');
      }
    });
    range.addEventListener('input', () => {
      this.finderWidth = Number(range.value);
      this.finderPreset = null;
      this.renderFinder();
    });
    this.finder.addEventListener('toggle', () => this.finder.open && this.renderFinder());
  }

  renderFinder() {
    if (!this.finder) return;
    const width = this.finderWidth ?? 200;
    this.finder.querySelector('[data-kkh-width-out]').textContent = `${width} cm`;
    this.finder.querySelectorAll('[data-kkh-furniture]').forEach((chip) => chip.setAttribute('aria-checked', String(chip.dataset.kkhFurniture === this.finderPreset)));
    const result = this.finder.querySelector('[data-kkh-finder-result]');
    const actions = this.finder.querySelector('[data-kkh-finder-actions]');
    const rec = recommendSize(this.sizes, (s) => this.outerCm(s), width);
    if (!rec) return;
    const pct = Math.round(rec.share * 100);
    const hasSet = Boolean(this.querySelector('[data-kkh-set]'));
    this.finderSetSize = null;
    if (rec.share < 0.4 && rec.set && hasSet) {
      this.finderSetSize = rec.set.size;
      result.innerHTML = `Even the largest print covers only ${pct}% of that width. Hang <strong>three at ${shortSize(rec.set.size)}</strong> instead: together they span ${Math.round(rec.set.spanCm)} cm.`;
      actions.innerHTML = `<button type="button" class="kkh-btn kkh-btn--primary kkh-btn--compact" data-kkh-see-set>See the set of three</button>`;
    } else if (rec.share < 0.4) {
      result.innerHTML = `Go for <strong>${shortSize(rec.single)}</strong>, the largest size. It covers ${pct}% of that width, so it will read as a focal point rather than fill the wall.`;
      actions.innerHTML = rec.single === this.state.size ? '' : `<button type="button" class="kkh-btn kkh-btn--ghost kkh-btn--compact" data-kkh-apply-size="${rec.single}">Choose ${shortSize(rec.single)}</button>`;
    } else {
      result.innerHTML = `Go for <strong>${shortSize(rec.single)}</strong>. At ${pct}% of the width it follows the two-thirds rule designers use${this.framed ? ', frame included' : ''}.`;
      actions.innerHTML = rec.single === this.state.size ? '' : `<button type="button" class="kkh-btn kkh-btn--ghost kkh-btn--compact" data-kkh-apply-size="${rec.single}">Choose ${shortSize(rec.single)}</button>`;
    }
  }

  /* ---------- Set of three ---------- */

  renderSet() {
    const set = this.querySelector('[data-kkh-set]');
    if (!set) return;
    const c = this.config;
    if (!this.setMembers) {
      this.setMembers = [...set.querySelectorAll('[data-kkh-set-member]')].map((el) => JSON.parse(el.textContent));
    }
    const setSize = this.roomSize ?? this.state.size;
    this.setSize = setSize;
    const picks = this.setMembers.map((member) =>
      member.variants.find(
        (v) => (!c.sizeOption || v.options[c.sizeOption - 1] === setSize) && (!c.frameOption || v.options[c.frameOption - 1] === this.state.format),
      ),
    );
    const complete = picks.every((v) => v?.available);
    const total = picks.reduce((sum, v) => sum + (v?.price ?? 0), 0);
    this.setPicks = complete ? picks : null;
    const outer = this.outerCm(setSize);
    const span = outer ? Math.round(outer.w * 3 + SET_GAP_CM * 2) : null;
    set.querySelector('[data-kkh-set-summary]').textContent = complete
      ? `Three at ${shortSize(setSize)}${this.framed ? ', framed' : ''}${span ? `, about ${span} cm wide when hung ${SET_GAP_CM} cm apart` : ''}.`
      : 'One of these prints is not available in this size.';
    const deal = c.setDiscount;
    const discounted = deal ? Math.round(total * (1 - deal.percent / 100)) : total;
    set.querySelector('[data-kkh-set-price]').textContent = complete ? this.money(discounted) : '';
    const was = set.querySelector('[data-kkh-set-was]');
    if (was) was.textContent = complete && deal ? this.money(total) : '';
    const button = set.querySelector('[data-kkh-set-add]');
    button.disabled = !complete;
    this.renderSetRoom();
  }

  /* The three prints hung together on the living-room wall, to scale. */
  renderSetRoom() {
    const set = this.querySelector('[data-kkh-set]');
    const host = set?.querySelector('[data-kkh-set-room]');
    const room = this.config.rooms?.living;
    const size = this.setSize ?? this.state.size;
    const outer = this.outerCm(size);
    if (!host || !room || !outer || !this.setMembers) return;
    // Always the tight crop around the sofa: the set column is narrow on every screen.
    const [cx0, cy0, cw, ch] = room.mobile;
    host.style.aspectRatio = `${cw} / ${ch}`;
    Object.assign(host.querySelector('[data-kkh-set-stage]').style, {
      width: `${(room.w / cw) * 100}%`,
      height: `${(room.h / ch) * 100}%`,
      left: `${(-cx0 / cw) * 100}%`,
      top: `${(-cy0 / ch) * 100}%`,
    });
    const img = host.querySelector('[data-kkh-set-room-img]');
    if (img.getAttribute('src') !== room.src) {
      img.width = room.w;
      img.height = room.h;
      img.src = room.src;
    }
    const w = outer.w / room.cmPerPx;
    const h = outer.h / room.cmPerPx;
    const gap = SET_GAP_CM / room.cmPerPx;
    const start = room.cx - (w * 3 + gap * 2) / 2;
    const bottom = room.base - 22 / room.cmPerPx;
    host.querySelectorAll('[data-kkh-set-hung]').forEach((el, i) => {
      Object.assign(el.style, {
        left: `${((start + i * (w + gap)) / room.w) * 100}%`,
        top: `${((bottom - h) / room.h) * 100}%`,
        width: `${(w / room.w) * 100}%`,
        height: `${(h / room.h) * 100}%`,
      });
      const face = el.querySelector('.kkh-face');
      const art = this.setMembers[i]?.art;
      if (face && art) this.paintFace(face, size, art);
    });
    const note = host.querySelector('[data-kkh-set-room-note]');
    if (note) note.textContent = `${room.note} · approximate scale`;
  }

  addSet() {
    if (!this.setPicks) return;
    track('kkh_set_add', { size: this.setSize ?? this.state.size, format: this.state.format });
    this.addToCart(this.setPicks.map((v) => ({ id: v.id, quantity: 1 })));
  }

  /* ---------- Wall studio ---------- */

  setWallTab(tab) {
    this.wallTab = tab === 'photo' ? 'photo' : 'camera';
    const dialog = this.sheets.wall;
    if (!dialog) return;
    dialog.querySelectorAll('[data-kkh-tab]').forEach((el) => el.setAttribute('aria-selected', String(el.dataset.kkhTab === this.wallTab)));
    dialog.querySelectorAll('[data-kkh-tabpanel]').forEach((el) => (el.hidden = el.dataset.kkhTabpanel !== this.wallTab));
    if (this.wallTab === 'camera') this.loadAr();
    else this.photoWall ??= dialog.querySelector('kkh-photo-wall')?.bind(this);
  }

  async artCanvas(maxEdge = 1400) {
    const { art } = this.config;
    if (!this.artBitmap) {
      this.artBitmap = new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = art.url;
      });
    }
    const img = await this.artBitmap;
    const crop = art.crop ?? { x: 0, y: 0, w: 1, h: 1 };
    const sx = crop.x * img.naturalWidth;
    const sy = crop.y * img.naturalHeight;
    const sw = crop.w * img.naturalWidth;
    const sh = crop.h * img.naturalHeight;
    const outer = this.outerCm();
    const print = this.printCm();
    const pxPerCm = maxEdge / Math.max(outer.w, outer.h);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(outer.w * pxPerCm);
    canvas.height = Math.round(outer.h * pxPerCm);
    const ctx = canvas.getContext('2d');
    if (this.framed) {
      const { frame } = this.config;
      ctx.fillStyle = frame.frameColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const f = frame.frameCm * pxPerCm;
      ctx.fillStyle = frame.matColor;
      ctx.fillRect(f, f, canvas.width - 2 * f, canvas.height - 2 * f);
      ctx.strokeStyle = 'rgba(0,0,0,0.08)';
      ctx.strokeRect(f, f, canvas.width - 2 * f, canvas.height - 2 * f);
      const m = (frame.frameCm + frame.matCm) * pxPerCm;
      ctx.drawImage(img, sx, sy, sw, sh, m, m, print.w * pxPerCm, print.h * pxPerCm);
    } else {
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    }
    return canvas;
  }

  async loadAr() {
    const wrap = this.querySelector('[data-kkh-ar]');
    if (!wrap) return;
    const key = `${this.state.size}|${this.state.format}`;
    if (this.arKey === key) return;
    this.arKey = key;
    const status = wrap.querySelector('[data-kkh-ar-status]');
    const launch = this.querySelector('[data-kkh-ar-launch]');
    const copy = this.querySelector('[data-kkh-ar-copy]');
    status.hidden = false;
    status.innerHTML = `${status.querySelector('svg')?.outerHTML ?? ''} Preparing the ${shortSize(this.state.size)} print`;
    try {
      const [canvas] = await Promise.all([this.artCanvas(), loadScript(MODEL_VIEWER_SRC, { module: true })]);
      await customElements.whenDefined('model-viewer');
      if (this.arKey !== key) return;
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      const outer = this.outerCm();
      const glb = buildPrintGlb({
        depthM: this.framed ? 0.025 : 0.003,
        edgeColor: this.framed ? this.config.frame.frameColor : '#fdfcfa',
        heightM: outer.h / 100,
        image: new Uint8Array(await blob.arrayBuffer()),
        widthM: outer.w / 100,
      });
      if (this.arUrl) URL.revokeObjectURL(this.arUrl);
      this.arUrl = URL.createObjectURL(new Blob([glb], { type: 'model/gltf-binary' }));
      let viewer = wrap.querySelector('model-viewer');
      if (!viewer) {
        viewer = document.createElement('model-viewer');
        Object.entries({
          alt: `${this.config.title}, shown at real size`,
          ar: '',
          'ar-modes': 'webxr scene-viewer quick-look',
          'ar-placement': 'wall',
          'ar-scale': 'fixed',
          'camera-controls': '',
          'camera-orbit': '0deg 90deg auto',
          'disable-pan': '',
          'environment-image': 'neutral',
          'interaction-prompt': 'none',
          'max-camera-orbit': '40deg 110deg auto',
          'min-camera-orbit': '-40deg 70deg auto',
          'shadow-intensity': '0.6',
          'touch-action': 'pan-y',
        }).forEach(([k, v]) => viewer.setAttribute(k, v));
        viewer.innerHTML = '<span slot="ar-button" hidden></span>';
        viewer.addEventListener('load', () => {
          status.hidden = true;
          const canAr = Boolean(viewer.canActivateAR);
          launch.hidden = !canAr;
          copy.hidden = !canAr;
          if (!canAr) this.showQr();
        });
        viewer.addEventListener('error', () => {
          status.hidden = false;
          status.textContent = 'The 3D view didn’t load. Try the photo option instead.';
        });
        viewer.addEventListener('ar-status', (event) => {
          if (event.detail?.status === 'failed') {
            copy.hidden = false;
            copy.textContent = 'Your camera view couldn’t start. Check camera permissions, or try the photo option.';
          }
          if (event.detail?.status === 'session-started') track('kkh_ar_start');
        });
        launch.addEventListener('click', () => {
          track('kkh_ar_launch');
          viewer.activateAR?.();
        });
        wrap.prepend(viewer);
      }
      copy.textContent = `Point your phone at the wall. The print appears at its real size, ${shortSize(this.state.size)}${this.framed ? ' with its frame' : ''}, and you can slide it into place.`;
      viewer.src = this.arUrl;
    } catch {
      this.arKey = null;
      status.hidden = false;
      status.textContent = 'The 3D view didn’t load. Try the photo option instead.';
    }
  }

  async showQr() {
    const box = this.querySelector('[data-kkh-qr]');
    if (!box) return;
    const url = new URL(this.config.productUrl, location.origin);
    if (this.variant) url.searchParams.set('variant', this.variant.id);
    url.hash = 'wall';
    try {
      await loadScript(QR_SRC);
      const qr = window.qrcode(0, 'M');
      qr.addData(url.toString());
      qr.make();
      box.querySelector('[data-kkh-qr-code]').innerHTML = qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
      box.hidden = false;
    } catch {
      box.hidden = true;
    }
  }
}

/* ---------- Photo of your wall ---------- */

class KkhPhotoWall extends HTMLElement {
  bind(print) {
    if (this.print) return this;
    this.print = print;
    this.stage = this.querySelector('[data-kkh-photo-stage]');
    this.img = this.querySelector('[data-kkh-photo-img]');
    this.handles = [...this.querySelectorAll('[data-kkh-handle]')];
    this.line = this.querySelector('[data-kkh-photo-line]');
    this.measure = this.querySelector('[data-kkh-photo-measure]');
    this.printEl = this.querySelector('[data-kkh-photo-print]');
    this.refCm = 80;
    this.points = [
      { x: 0.35, y: 0.7 },
      { x: 0.65, y: 0.7 },
    ];
    this.position = { x: 0.5, y: 0.35 };
    this.step = 'empty';

    this.querySelectorAll('[data-kkh-photo-input]').forEach((input) =>
      input.addEventListener('change', () => {
        const file = input.files?.[0];
        if (file) this.load(file);
        input.value = '';
      }),
    );
    this.addEventListener('click', (event) => {
      const ref = event.target.closest('[data-kkh-ref]');
      if (ref) {
        this.refCm = Number(ref.dataset.kkhRef);
        this.querySelector('[data-kkh-ref-input]').value = this.refCm;
        this.refresh();
      }
      if (event.target.closest('[data-kkh-photo-place]')) {
        this.setStep('place');
        track('kkh_photo_place');
      }
      if (event.target.closest('[data-kkh-photo-remeasure]')) this.setStep('measure');
    });
    this.querySelector('[data-kkh-ref-input]').addEventListener('input', (event) => {
      const value = Number(event.target.value);
      if (value > 0) {
        this.refCm = value;
        this.refresh();
      }
    });
    this.handles.forEach((handle, index) => this.draggable(handle, (point) => (this.points[index] = point)));
    this.draggable(this.printEl, (point) => (this.position = point), true);
    new ResizeObserver(() => this.refresh()).observe(this.stage);
    return this;
  }

  draggable(el, onMove, offset = false) {
    let start = null;
    el.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      el.setPointerCapture(event.pointerId);
      const rect = this.stage.getBoundingClientRect();
      const current = offset ? this.position : null;
      start = { rect, dx: current ? current.x - (event.clientX - rect.left) / rect.width : 0, dy: current ? current.y - (event.clientY - rect.top) / rect.height : 0 };
    });
    el.addEventListener('pointermove', (event) => {
      if (!start || !el.hasPointerCapture(event.pointerId)) return;
      const { rect, dx, dy } = start;
      const clamp = (v) => Math.min(1, Math.max(0, v));
      onMove({ x: clamp((event.clientX - rect.left) / rect.width + dx), y: clamp((event.clientY - rect.top) / rect.height + dy) });
      this.refresh();
    });
    const end = () => (start = null);
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
  }

  load(file) {
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = URL.createObjectURL(file);
    this.img.onload = () => {
      this.setStep('measure');
      track('kkh_photo_upload');
    };
    this.img.src = this.url;
  }

  setStep(step) {
    this.step = step;
    this.querySelector('[data-kkh-photo-empty]').hidden = step !== 'empty';
    this.stage.hidden = step === 'empty';
    this.querySelectorAll('[data-kkh-photo-step]').forEach((el) => (el.hidden = el.dataset.kkhPhotoStep !== step));
    const measuring = step === 'measure';
    this.handles.forEach((h) => (h.hidden = !measuring));
    this.line.hidden = !measuring;
    this.measure.hidden = !measuring;
    this.printEl.hidden = step !== 'place';
    this.refresh();
  }

  refresh() {
    if (!this.print || this.step === 'empty' || !this.stage) return;
    const rect = this.stage.getBoundingClientRect();
    if (!rect.width) return;
    const [a, b] = this.points;
    this.handles.forEach((h, i) => {
      h.style.left = `${this.points[i].x * 100}%`;
      h.style.top = `${this.points[i].y * 100}%`;
    });
    const line = this.line.querySelector('line');
    line.setAttribute('x1', a.x * rect.width);
    line.setAttribute('y1', a.y * rect.height);
    line.setAttribute('x2', b.x * rect.width);
    line.setAttribute('y2', b.y * rect.height);
    this.measure.style.left = `${((a.x + b.x) / 2) * 100}%`;
    this.measure.style.top = `${(Math.min(a.y, b.y)) * 100}%`;
    this.measure.textContent = `${this.refCm} cm`;
    this.querySelectorAll('[data-kkh-ref]').forEach((chip) => chip.setAttribute('aria-checked', String(Number(chip.dataset.kkhRef) === this.refCm)));

    const distance = Math.hypot((b.x - a.x) * rect.width, (b.y - a.y) * rect.height);
    const pxPerCm = distance / this.refCm;
    const outer = this.print.outerCm();
    if (!outer || !pxPerCm) return;
    const w = outer.w * pxPerCm;
    const h = outer.h * pxPerCm;
    this.printEl.style.width = `${w}px`;
    this.printEl.style.height = `${h}px`;
    this.printEl.style.left = `${this.position.x * rect.width - w / 2}px`;
    this.printEl.style.top = `${this.position.y * rect.height - h / 2}px`;
    this.print.paintFace(this.printEl.querySelector('[data-kkh-face]'));
  }
}

customElements.define('kkh-photo-wall', KkhPhotoWall);
customElements.define('kkh-print', KkhPrint);
